<template>
    <div class="field">
        <label class="field__title"
               for="email-input">
            <Translate translationKey="quests-dashboard.emailAddress"
                       fallback="Email address" />
        </label>
        <input id="email-input"
               :value="modelValue"
               class="field__input"
               :class="backendError ? 'field__input--error' : ''"
               type="email"
               :aria-label="getTrans('quests-dashboard.enterEmailHere', 'Enter email here')"
               :placeholder="placeholder"
               @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
               @focus="removePlaceholder"
               @blur="restorePlaceholder">
        <div v-if="backendError"
             class="field__error-message">
            {{ backendError }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, watch, computed } from 'vue';
    import { getTrans } from '@/js/helpers/getTrans';
    import { validateEmail } from '@/js/helpers/emailValidations';

    const props = defineProps<{
        modelValue: string;
        initialEmail?: string | null;
        isEmailVerified: boolean;
        backendError: string | null;
    }>();

    const emit = defineEmits<{(e: 'update:modelValue', value: string): void;
                              (e: 'emailSubmitAvailable', state: boolean): void;
    }>();

    const defaultPlaceholder = getTrans('quests-dashboard.enterEmailHere');
    const placeholder = ref(defaultPlaceholder);
    const emailValid = ref(false);
    const areEmailsEqualIgnoreCase = ref(true);

    const emailSubmitAvailable = computed(() => emailValid.value && (!props.isEmailVerified || !areEmailsEqualIgnoreCase.value));

    const removePlaceholder = () => {
        placeholder.value = '';
    };

    const restorePlaceholder = (event: FocusEvent) => {
        const target = event.target as HTMLInputElement;
        if (!target.value) {
            placeholder.value = defaultPlaceholder;
        }
    };

    watch(() => props.modelValue, (newValue) => {
        emailValid.value = validateEmail(newValue);
        if (props.initialEmail) {
            areEmailsEqualIgnoreCase.value = newValue.toLowerCase() === props.initialEmail.toLowerCase();
        } else {
            areEmailsEqualIgnoreCase.value = false; // Can't be equal if initial is null/undefined
        }
    }, { immediate: true });

    watch(() => emailSubmitAvailable.value, (newValue) => {
        emit('emailSubmitAvailable', newValue);
    }, { immediate: true });
</script>

<style scoped lang="scss">
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.field {
    @include modalMixins.modal-field;

    & {
        width: 100%;
        margin: 40px 0;
    }
}
</style>
